import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMe } from "@/auth/use-me";
import { useChatStore } from "@/stores/chat-store";
import { DeleteConversationDocument, PersonsByIdsDocument } from "@/graphql/generated/operations";
import { Avatar, type PersonInfo } from "@/components/common/Avatar";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ChatView } from "./ChatView";
import { logApplicationFailure } from "@/lib/user-error";

interface ChatWindowProps {
  conversationId: string;
}

// Resolves the other participant of a 1:1 conversation for the window header.
// Self-chat (only myId in participantIds) falls back to the current user's
// own avatar + a "You" label, mirroring how ConversationList renders self-rows.
function useOther(conversationId: string): PersonInfo {
  const intl = useIntl();
  const me = useMe();
  const myId = useChatStore((s) => s.myId);
  const convo = useChatStore((s) => s.conversations.get(conversationId));

  const otherIds = useMemo(() => {
    if (!convo || !myId) return [];
    return convo.participantIds.filter((id) => id !== myId);
  }, [convo, myId]);

  const isSelfChat = convo !== undefined && myId !== null && otherIds.length === 0;

  const { data } = useQuery(PersonsByIdsDocument, {
    variables: { input: { first: 10, where: { id: { in: otherIds } } } },
    skip: otherIds.length === 0,
  });

  if (isSelfChat) {
    return {
      name: intl.formatMessage({ id: "chat.you", defaultMessage: "You" }),
      avatarContent: me.avatarContent ?? null,
      avatarMime: me.avatarMime ?? null,
    };
  }

  const node = data?.persons.nodes?.[0];
  return {
    name:
      node?.formattedName ??
      intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" }),
    avatarContent: node?.avatar?.content,
    avatarMime: node?.avatar?.mimeType,
  };
}

// Live online state of the (single) other participant of a 1:1 chat.
function useOtherOnline(conversationId: string): boolean {
  const myId = useChatStore((s) => s.myId);
  const otherId = useChatStore((s) =>
    s.conversations.get(conversationId)?.participantIds.find((id) => id !== myId),
  );
  return (
    useChatStore((s) =>
      otherId ? s.presence.get(conversationId)?.get(otherId)?.online : undefined,
    ) ?? false
  );
}

function IconBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-7 w-7 place-items-center rounded-md text-ink/45 transition hover:bg-ink/[0.06] hover:text-ink"
    >
      {children}
    </button>
  );
}

// A minimized conversation: header-only bar that restores on click.
export function MinimizedChat({ conversationId }: ChatWindowProps) {
  const intl = useIntl();
  const other = useOther(conversationId);
  const online = useOtherOnline(conversationId);
  const openChat = useChatStore((s) => s.openChat);
  const closeChat = useChatStore((s) => s.closeChat);
  const unread = useChatStore((s) => s.conversations.get(conversationId)?.unreadCount ?? 0);
  const hasUnread = unread > 0;

  return (
    <div
      className={`pointer-events-auto flex w-[220px] shrink-0 items-center gap-2 rounded-t-xl border border-b-0 px-3 py-2 shadow-lg ${
        hasUnread ? "border-accent/70 bg-ink/[0.06] ring-2 ring-accent/25" : "border-ink/10 bg-surface"
      }`}
    >
      <button
        type="button"
        onClick={() => openChat(conversationId)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <Avatar
          person={other}
          size="sm"
          online={online}
          onlineLabel={intl.formatMessage({ id: "chat.online", defaultMessage: "Online" })}
        />
        <span className={`truncate text-sm text-ink ${hasUnread ? "font-bold" : "font-medium"}`}>
          {other.name}
        </span>
        {hasUnread && (
          <span
            className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-semibold text-on-accent"
            aria-label={intl.formatMessage(
              { id: "chat.unreadMessages", defaultMessage: "{count} unread" },
              { count: unread },
            )}
            title={intl.formatMessage(
              { id: "chat.unreadMessages", defaultMessage: "{count} unread" },
              { count: unread },
            )}
          >
            {unread}
          </span>
        )}
      </button>
      <IconBtn
        label={intl.formatMessage({ id: "chat.closeChat", defaultMessage: "Close chat" })}
        onClick={() => closeChat(conversationId)}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </IconBtn>
    </div>
  );
}

// An open conversation window: header + message thread + composer.
export function ChatWindow({ conversationId }: ChatWindowProps) {
  const intl = useIntl();
  const other = useOther(conversationId);
  const online = useOtherOnline(conversationId);
  const minimizeChat = useChatStore((s) => s.minimizeChat);
  const closeChat = useChatStore((s) => s.closeChat);
  const removeConversation = useChatStore((s) => s.removeConversation);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConversation] = useMutation(DeleteConversationDocument);

  async function handleDelete() {
    try {
      await deleteConversation({ variables: { conversationId } });
      // Remove from the store directly. MyConversations now merges (it
      // never drops entries), so we can't lean on a refetch to delete;
      // removeConversation also closes the window. The lifecycle DELETED
      // event for our own action is then a harmless no-op.
      removeConversation(conversationId);
    } catch {
      logApplicationFailure("chat.delete-conversation");
    } finally {
      setConfirmDelete(false);
    }
  }

  return (
    <div className="pointer-events-auto flex h-[70dvh] w-[calc(100vw-1.5rem)] shrink-0 flex-col rounded-t-xl border border-b-0 border-ink/10 bg-surface shadow-2xl sm:h-[460px] sm:w-[336px]">
      <div className="flex items-center gap-2 border-b border-ink/10 px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Avatar
            person={other}
            size="sm"
            online={online}
            onlineLabel={intl.formatMessage({ id: "chat.online", defaultMessage: "Online" })}
          />
          <span className="truncate text-sm font-semibold text-ink">{other.name}</span>
        </div>
        <div className="flex shrink-0 items-center">
          <div className="relative">
            <IconBtn
              label={intl.formatMessage({ id: "chat.more", defaultMessage: "More" })}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.6" />
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="12" cy="19" r="1.6" />
              </svg>
            </IconBtn>
            {menuOpen ? (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full z-30 mt-1 min-w-[150px] rounded-md bg-surface p-1 shadow-lg ring-1 ring-ink/10">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                    className="block w-full rounded px-3 py-1.5 text-left text-[12px] text-red-600 transition hover:bg-red-50"
                  >
                    <FormattedMessage id="chat.deleteChat" defaultMessage="Delete chat" />
                  </button>
                </div>
              </>
            ) : null}
          </div>
          <IconBtn
            label={intl.formatMessage({ id: "chat.minimize", defaultMessage: "Minimize" })}
            onClick={() => minimizeChat(conversationId)}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
            </svg>
          </IconBtn>
          <IconBtn
            label={intl.formatMessage({ id: "chat.closeChat", defaultMessage: "Close chat" })}
            onClick={() => closeChat(conversationId)}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </IconBtn>
        </div>
      </div>
      <ChatView conversationId={conversationId} />

      <ConfirmDialog
        open={confirmDelete}
        title={<FormattedMessage id="chat.deleteChat" defaultMessage="Delete chat" />}
        message={
          <FormattedMessage
            id="chat.deleteChatConfirm"
            defaultMessage="Delete this conversation?"
          />
        }
        confirmLabel={<FormattedMessage id="common.delete" defaultMessage="Delete" />}
        cancelLabel={<FormattedMessage id="common.cancel" defaultMessage="Cancel" />}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
